import { createApi } from "@reduxjs/toolkit/query/react";
import { 
    createItemOpeningStock, 
    listAllItemOpeningStock, 
    listItemwiseStockSummary, 
    listStockSummary,
    listItemwiseStockSummaryv2,
    listStockSummaryv2,
    ListStockSummaryQuery,
    ListStockSummaryQueryVariables,
    ListItemwiseStockSummaryQuery,
    ListItemwiseStockSummaryQueryVariables,
    ListStockSummaryv2Query,
    ListStockSummaryv2QueryVariables,
    ListItemwiseStockSummaryv2Query,
    ListItemwiseStockSummaryv2QueryVariables,
    CreateItemOpeningStockMutation,
    CreateItemOpeningStockMutationVariables,
    ListAllItemOpeningStockQuery,
} from "../../../graphql";
import { 
    ProcessedStockSummary,
    ProcessedItemWiseStockSummary,
} from "../types/graphql";
import { baseQuery } from "../../../utils";

export const stockManagementApi = createApi({
    reducerPath: "stockManagementApi",
    baseQuery: baseQuery,
    tagTypes: ["listStockSummary", "listStockSummaryByItemId", "listItemOpeningStock", "listStockSummaryv2", "listItemwiseStockSummaryv2"],
    endpoints: (builder) => ({
        listStockSummary: builder.query<ProcessedStockSummary[], ListStockSummaryQueryVariables>({
            query: (variables) => ({ body: listStockSummary, variables }),
            transformResponse: (response: ListStockSummaryQuery): ProcessedStockSummary[] => {
                return (response?.listStockSummary ?? [])
                    .filter((item): item is NonNullable<typeof item> => item != null)
                    .map((item) => ({
                        slno: item.slno,
                        itemid: String(item.itemid ?? ''),
                        itemcode: item.itemcode,
                        itemname: item.itemname,
                        unit: item.unit,
                        hsn: item.hsn,
                        ...(item.taxability != null && { taxability: item.taxability }),
                        gstrate: item.gstrate,
                        openingamt: item.openingamt,
                        openingqty: item.openingqty,
                        purchaseamt: item.purchaseamt,
                        purchaseqty: item.purchaseqty,
                        salesamt: item.salesamt,
                        salesqty: item.salesqty,
                        closingamt: item.closingamt,
                        closingqty: item.closingqty,
                    }));
            },
            keepUnusedDataFor: 300,
            providesTags: ["listStockSummary"],
        }),
        listStockSummaryv2: builder.query<ProcessedStockSummary[], ListStockSummaryv2QueryVariables>({
            query: (variables) => ({ body: listStockSummaryv2, variables }),
            transformResponse: (response: ListStockSummaryv2Query): ProcessedStockSummary[] => {
                return (response?.listStockSummaryv2 ?? [])
                    .filter((item): item is NonNullable<typeof item> => item != null)
                    .map((item) => ({
                        slno: item.slno,
                        itemid: String(item.itemid ?? ''),
                        itemcode: item.itemcode,
                        itemname: item.itemname,
                        unit: item.unit,
                        hsn: item.hsn,
                        ...(item.taxability != null && { taxability: item.taxability }),
                        gstrate: item.gstrate,
                        openingamt: item.openingamt,
                        openingqty: item.openingqty,
                        purchaseamt: item.purchaseamt,
                        purchaseqty: item.purchaseqty,
                        salesamt: item.salesamt,
                        salesqty: item.salesqty,
                        closingamt: item.closingamt,
                        closingqty: item.closingqty,
                    }));
            },
            keepUnusedDataFor: 300,
            providesTags: ["listStockSummaryv2"],
        }),
        listStockSummaryByItemId: builder.query<ProcessedItemWiseStockSummary[], ListItemwiseStockSummaryQueryVariables>({
            query: (variables) => ({ body: listItemwiseStockSummary, variables }),
            transformResponse: (response: ListItemwiseStockSummaryQuery): ProcessedItemWiseStockSummary[] => {
                return (response?.listItemwiseStockSummary ?? []).map((item) => ({
                    itemid: String(item.itemid ?? ''),
                    date: item.date ?? 0,
                    particular: item.particular ?? '',
                    voucher_no: item.voucher_no ?? '',
                    qty: item.qty ?? 0,
                    cost_item: item.cost_item ?? 0,
                    total_cost: item.total_cost ?? 0,
                    closing_stock: item.closing_stock ?? 0,
                    closing_stock_amount: item.closing_stock_amount ?? 0,
                    invoiceid: String(item.invoiceid ?? ''),
                    ...(item.next_cursor_timestamp != null && { next_cursor_timestamp: item.next_cursor_timestamp }),
                    ...(item.next_cursor_transactionid != null && { next_cursor_transactionid: item.next_cursor_transactionid }),
                    ...(item.total_count != null && { total_count: item.total_count }),
                }));
            },
            keepUnusedDataFor: 300,
            providesTags: ["listStockSummaryByItemId"],
        }),
        listItemwiseStockSummaryv2: builder.query<ProcessedItemWiseStockSummary[], ListItemwiseStockSummaryv2QueryVariables>({
            query: (variables) => ({ body: listItemwiseStockSummaryv2, variables }),
            transformResponse: (response: ListItemwiseStockSummaryv2Query): ProcessedItemWiseStockSummary[] => {
                return (response?.listItemwiseStockSummaryv2 ?? []).map((item) => ({
                    itemid: String(item.itemid ?? ''),
                    date: item.date ?? 0,
                    particular: item.particular ?? '',
                    voucher_no: item.voucher_no ?? '',
                    qty: item.qty ?? 0,
                    cost_item: item.cost_item ?? 0,
                    total_cost: item.total_cost ?? 0,
                    closing_stock: item.closing_stock ?? 0,
                    closing_stock_amount: item.closing_stock_amount ?? 0,
                    invoiceid: String(item.invoiceid ?? ''),
                    remark: item.remark ?? '',
                    ...(item.next_cursor_timestamp != null && { next_cursor_timestamp: item.next_cursor_timestamp }),
                    ...(item.next_cursor_transactionid != null && { next_cursor_transactionid: item.next_cursor_transactionid }),
                    ...(item.total_count != null && { total_count: item.total_count }),
                }));
            },
            keepUnusedDataFor: 300,
            providesTags: ["listItemwiseStockSummaryv2"],
        }),
        createItemOpeningStock: builder.mutation<CreateItemOpeningStockMutation, CreateItemOpeningStockMutationVariables>({
            query: (variables) => ({ body: createItemOpeningStock, variables }),
            invalidatesTags: ["listStockSummary", "listItemOpeningStock"],
        }),
        listItemOpeningStock: builder.query<NonNullable<NonNullable<ListAllItemOpeningStockQuery['listAllItemOpeningStock']>[number]>[], void>({
            query: () => ({ body: listAllItemOpeningStock, variables: {} }),
            transformResponse: (response: ListAllItemOpeningStockQuery): NonNullable<NonNullable<ListAllItemOpeningStockQuery['listAllItemOpeningStock']>[number]>[] => {
                return (response?.listAllItemOpeningStock ?? [])
                    .filter((item): item is NonNullable<typeof item> => item != null);
            },
            keepUnusedDataFor: 300,
            providesTags: ["listItemOpeningStock"],
        }),
    }),
});

export const {
    useListStockSummaryQuery,
    useListStockSummaryv2Query,
    useCreateItemOpeningStockMutation,
    useListStockSummaryByItemIdQuery,
    useListItemwiseStockSummaryv2Query,
    useListItemOpeningStockQuery
} = stockManagementApi;
